import React, { MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react';
import { FieldProps } from "formik/dist/Field";
import { Field, FieldHelperProps, FieldMetaProps, FieldValidator, FormikErrors, useField } from "formik";
import { PdsSelect } from "@principal/design-system-react";
import 'select2/dist/css/select2.min.css'

var _ = require('lodash');

export type SelectDropdownProps = {
    id: string,
    name?: string,
    label: string,
    labelContent?: ReactElement,
    required: boolean,
    options: SelectDropdownOption[],
    multiple?: boolean,
    changeCallback?: Function,
    validate?: FieldValidator,
    children?: React.ReactNode,
    disabled?: boolean
}

export type SelectDropdownOption = {
    id: string,
    text: string,
    value?: string,
    disabled?: boolean
}

const SelectDropdown = (props: SelectDropdownProps) => {
    const id = props.id;
    const multiple = props.multiple;
    const name = props.name ? props.name : _.camelCase(id);
    const options = props.options;
    const [, meta, helpers] = useField({
        name,
        validate: validateInput
    });
    const [initialValue] = useState(meta.initialValue)
    const setValue = helpers.setValue;
    const inputRef = useRef(null as any as HTMLElement)
    const fieldRemoved = meta.initialValue === undefined;

    useEffect(() => {
        if(!multiple) {
            return;
        }
        const timer = setInterval(() => {
            // @ts-ignore
            let jquerySelect = window.$(`#${id}`);
            if(jquerySelect.length) {
                initializeSelect2(jquerySelect, setValue)
                clearInterval(timer)
            }
        }, 200);
        return () => clearInterval(timer);
    }, [id, multiple, setValue]);

    useEffect(() => {
        return () => {
            helpers.setValue(initialValue)
        }
    }, [helpers, initialValue]);

    const onlyOneRequiredOption = options.length === 1 && props.required;
    const firstOptionValue = findValueFor(options[0])
    if(onlyOneRequiredOption && meta.value !== firstOptionValue) {
        setValue(firstOptionValue);
    }

    let dropdownProps = props;
    if(onlyOneRequiredOption) {
        dropdownProps = { ...props, disabled: true }
    }

    return (fieldRemoved ? null :
            <div className="pds-u-margin-bottom-16">
                {createSelect(dropdownProps, name, helpers, meta, inputRef)}
            </div>
    )

    function validateInput(value: string) {
        const inputRefCurrent = inputRef?.current;
        if(fieldRemoved || !inputRefCurrent) {
            return '';
        }
        const hasValue = Array.isArray(value) ? value.length > 0: value;
        const priorOrCurrentStep = inputRefCurrent?.closest('.prior-step, .current-step');
        const invisible = inputRefCurrent?.closest('.invisible');
        const required = props.required;
        if((priorOrCurrentStep && invisible && !hasValue) || inputRefCurrent?.closest('.future-step')) {
            return '';
        } else if(hasValue) {
            return props.validate?.(value)
        } else if(required) {
            return 'Please fill out this field.'
        }
        return '';
    }
};

const Select = ({ field, form, helpers, meta, inputRef, ...props }: FieldProps & SelectDropdownProps & {meta: FieldMetaProps<any>, helpers: FieldHelperProps<any>, inputRef: MutableRefObject<any>}) => {
    const multiple = props.multiple;
    const error = meta.error
    const Component = multiple ? 'select' : PdsSelect;
    const label =
        <label slot="label" data-field-name={field.name} data-field-label={props.label} htmlFor={props.id}>
            {props.label}{props.labelContent}
        </label>
    return (
        <>
            {multiple && <div>{label}</div>}
            <Component {...field} id={props.id} fieldId={props.id}
                       label={props.label}
                       multiple={multiple}
                       required={props.required}
                       style={{width: '100%'}}
                       errorMessage={(error && !(error as unknown as FormikErrors<any>).hidden) ? error as string : undefined}
                       ref={inputRef}
                       disabled={props.disabled}
                       onChange={(event) => {
                           helpers.setError(undefined)
                           if (field) {
                               field.onChange(event);
                           }
                           if (props.changeCallback) {
                               props.changeCallback(event.target)
                           }
                       }}>
                {!multiple && label}
                {buildOptions(props)}
            </Component>
        </>
    );
};

function createSelect(props: SelectDropdownProps, name: string, helpers: FieldHelperProps<any>, meta:FieldMetaProps<any>, inputRef: MutableRefObject<any>) {
    return <Field component={Select} {...props} name={name} helpers={helpers} meta={meta} inputRef={inputRef}/>
}

function initializeSelect2(jquerySelect: any, setValue: (value: any, shouldValidate?: boolean) => void) {
    jquerySelect.select2({
        "placeholder": "Select all that apply",
        "closeOnSelect": false,
        "multiple": true,
        "selectionCssClass": "multi-select"
    })
    jquerySelect.on('change', () => {
        setValue(jquerySelect.val())
    });
}

function buildOptions(props: SelectDropdownProps) {
    let builtOptions: ReactElement[] = [<option key={props.id + '-blank'} id={props.id + '-blank'} value=''/>];
    props.options.forEach((option) => {
        builtOptions.push(<option key={props.id + '-' + option.id} id={props.id + '-' + option.id}
                                  value={findValueFor(option)} disabled={option.disabled}>{option.text}</option>)
    })
    return builtOptions;
}

function findValueFor(option: SelectDropdownOption): string {
    return option?.value ?? option?.text;
}
export default SelectDropdown;
